import { useState, useCallback, useEffect } from 'react';
import { useUploadReferralMultipleDocuments } from '../../redux/uploadReferralMultipleDocuments';
import { useMutation } from '@apollo/client';
import clientGraphql from '../../../../common/apollo-graphql';
import { DELETE_REFERRAL_DOCUMENT, UPDATE_REFERRAL_DOCUMENT } from '../../gql/Mutation';

export function useDocumentUploads({
  referralUuid,
  refetch,
  uploadedFileObjects = [],
  onSuccess = () => { },
  onError = () => { },
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { uploadReferralMultipleDocuments } = useUploadReferralMultipleDocuments();
  const [uploads, setUploads] = useState({});

  // GraphQL mutation to delete a referral document
  const [deleteReferralDocument] = useMutation(DELETE_REFERRAL_DOCUMENT, {
    client: clientGraphql,
  });

  const [renameReferralDocument] = useMutation(UPDATE_REFERRAL_DOCUMENT, { 
    client: clientGraphql 
  });


  // Initialize uploads with server files
  useEffect(() => {
    if (uploadedFileObjects.length) {
      const initial = {};
      uploadedFileObjects.forEach((f) => {
        const docId = f.name || 'misc';
        if (!initial[docId]) initial[docId] = [];
        initial[docId].push({
          url: f.url,
          displayName: f.referral_document_name,
          name: f.name,
          status: 'done',
          id: f.url, // using URL as unique ID for server files
          serverId: f.id, // include server ID for deletion]
          uuid: f.uuid,
        });
      });
      setUploads(initial);
    } else {
      setUploads([]);
    }
  }, [uploadedFileObjects]);

  const addFiles = useCallback(
    (docId, files, renameDoc = []) => {
      const fileObjs = Array.from(files).map((f, i) => ({
        file: f,
        referral_document_name: renameDoc[i]?.referral_document_name || f.name,
        progress: 0,
        status: 'pending',
        displayName: f.name,
        id: `${docId}-${Date.now()}-${i}`,
      }));

      setIsLoading(true);

      fileObjs.forEach((fileObj, index) => {
        const formData = new FormData();
        if (docId === 'misc') {
          formData.append('files[]', fileObj.file, fileObj.referral_document_name);
          formData.append('use_filename', true);
        } else if (docId === 'order_documents') {
          docId = -1;
          formData.append('files[]', fileObj.file, fileObj.referral_document_name);
          formData.append('use_filename', true);
        } else {
          formData.append('files[]', fileObj.file, docId);
        }
        formData.append('document_id', docId === 'misc' ? 0 : Number(docId));
        formData.append('type', 'internal');

        uploadReferralMultipleDocuments(referralUuid, formData)
          .then(() => {
            refetch();
            onSuccess();
          })
          .catch(() => {
            onError();
          })
          .finally(() => {
            setIsLoading(false);
          });;
      });
    },
    [referralUuid, uploadReferralMultipleDocuments, refetch, onSuccess, onError]
  );

  const removeFile = useCallback(
    async (docId, fileObj) => {
      setIsLoading(true);
      setUploads((prev) => ({
        ...prev,
        [docId]: (prev[docId] || []).filter((f) => f.id !== fileObj.id),
      }));

      try {
        await deleteReferralDocument({
          variables: { referralUuid: referralUuid, referralDocumentUuid: fileObj.uuid }
        });
        refetch();
      } catch (err) {
        console.error('Failed to delete referral document:', err);
        onError();
      } finally {
        setIsLoading(false);
      }
    },
    [deleteReferralDocument, referralUuid, refetch, onError]
  );

  const renameFile = async (fileObj, newName) => {
    try {
      setIsLoading(true);

      const { data } = await renameReferralDocument({
        variables: {
          referralUuid,
          referralDocumentUuid: fileObj.uuid,
          referralDocument: { name: newName },
        },
      });

      if (!data?.save_referral_document) throw new Error('Failed to rename file');

      refetch();
    } catch (err) {
      console.error(err);
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return { uploads, addFiles, removeFile, renameFile, setUploads, isLoading };
}
